import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../axiosBaseQuery";
import {
    InternalToolActivityEntry,
    InternalToolActivityParams,
    InternalToolActivityResponse,
} from "@callveriq/type/internalTools/Activity";
import {
    clampActivityLimit,
    INTERNAL_TOOL_ACTIVITY_TAG,
    INTERNAL_TOOL_TAG_TYPES,
    INTERNAL_TOOLS_ACTIVITY_URL,
    INTERNAL_TOOLS_REDUCER_PATH,
    sanitizeInternalToolRequest,
} from "./constants";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const asString = (value: unknown): string =>
    typeof value === "string"
        ? value
        : typeof value === "number"
        ? String(value)
        : "";

const pick = (sources: UnknownRecord[], key: string): unknown => {
    for (const source of sources) {
        if (source[key] !== undefined && source[key] !== null) {
            return source[key];
        }
    }
    return undefined;
};

const normalizeActivityEntry = (
    raw: unknown,
    index: number
): InternalToolActivityEntry | null => {
    if (!isRecord(raw)) return null;
    // Accept both a flattened row and a raw ActivityLog row whose audit
    // payload sits in `changes` (and whose user is the nested `person`).
    const changes = isRecord(raw.changes) ? raw.changes : {};
    const sources = [raw, changes];
    const person = isRecord(raw.person) ? raw.person : {};
    const objectId = pick(sources, "object_id");
    return {
        id: (pick([raw], "id") as number | string | undefined) ?? index,
        tool: asString(pick(sources, "tool")),
        action: asString(pick(sources, "action")).toUpperCase(),
        status: asString(pick(sources, "status")).toUpperCase(),
        internal_user:
            asString(pick(sources, "internal_user")) ||
            asString(pick(sources, "user_email")) ||
            asString(person.email),
        trace_id: asString(pick(sources, "trace_id")),
        created_at: asString(pick(sources, "created_at")),
        object_id: typeof objectId === "number" ? objectId : null,
        details: pick(sources, "details") ?? null,
    };
};

/**
 * Normalises the activity response. Accepts a bare list, `{results}`,
 * `{data}` or `{items}`, with flattened or raw `ActivityLog` rows.
 */
export const normalizeInternalToolActivityResponse = (
    response: unknown
): InternalToolActivityResponse => {
    const container = isRecord(response) ? response : {};
    const list = Array.isArray(response)
        ? response
        : [container.results, container.data, container.items].find(
              Array.isArray
          ) ?? [];
    return {
        subdomain:
            typeof container.subdomain === "string"
                ? container.subdomain
                : null,
        results: (list as unknown[])
            .map(normalizeActivityEntry)
            .filter(
                (entry): entry is InternalToolActivityEntry => entry !== null
            ),
    };
};

const defaultBaseQuery = axiosBaseQuery();

/**
 * The default `axiosBaseQuery()` (current domain's API host, JWT) with every
 * request passed through `sanitizeInternalToolRequest`, which drops
 * `subdomain`, `tenant`, `tenant_name`, `current_user` and `requested_by`
 * from the body, params and query string.
 */
const internalToolsBaseQuery: typeof defaultBaseQuery = (
    args,
    api,
    extraOptions
) => defaultBaseQuery(sanitizeInternalToolRequest(args), api, extraOptions);

/**
 * The one RTK Query slice shared by every Settings -> Internal Tools page.
 *
 * Tools add their endpoints from their own file
 * (`redux/services/settings/internalTools/<dir>.service.ts`) with
 * `internalToolsApiSlice.injectEndpoints(...)`. The base query targets the
 * current domain's API host, so requests never carry a subdomain (and any
 * tenant / actor field a page adds is stripped before sending).
 * The global error toast is skipped for this slice (see `errorhandler.js`):
 * pages show their own errors through `getApiErrorMessage`.
 */
export const internalToolsApiSlice = createApi({
    reducerPath: INTERNAL_TOOLS_REDUCER_PATH,
    baseQuery: internalToolsBaseQuery,
    tagTypes: INTERNAL_TOOL_TAG_TYPES,
    endpoints: (builder) => ({
        getInternalToolActivity: builder.query<
            InternalToolActivityResponse,
            InternalToolActivityParams
        >({
            query: ({ tool, limit }) => ({
                url: INTERNAL_TOOLS_ACTIVITY_URL,
                method: "GET",
                params: { tool, limit: clampActivityLimit(limit) },
            }),
            transformResponse: normalizeInternalToolActivityResponse,
            providesTags: (_result, _error, { tool }) => [
                { type: INTERNAL_TOOL_ACTIVITY_TAG, id: tool },
            ],
        }),
    }),
});

export const { useGetInternalToolActivityQuery } = internalToolsApiSlice;
